import { CardChoice, EmptyDomino, GameEvent } from "./types";
import { getSuits, isDouble } from "./dominoMap";
import { Player } from "./rules";

export class ComputerPlayer implements Player {
  private hand: number[] = [];
  constructor(private name: string) {}

  getName(): string {
    return this.name;
  }
  getHand(): number[] {
    return this.hand;
  }
  setHand(h: number[]): void {
    this.hand = h.slice();
  }

  async playCard(event: GameEvent): Promise<CardChoice> {
    const board = event.board!;
    const candidates: { card: number; side: "left" | "right" }[] = [];
    const doubles: { card: number; side: "left" | "right" }[] = [];
    for (const d of this.hand) {
      if (d === EmptyDomino) continue;
      const [s1, s2] = getSuits(d);
      const fitsLeft = s1 === board.leftSuit || s2 === board.leftSuit;
      const fitsRight = s1 === board.rightSuit || s2 === board.rightSuit;
      if (fitsLeft) {
        (isDouble(d) ? doubles : candidates).push({ card: d, side: "left" });
      }
      if (fitsRight) {
        (isDouble(d) ? doubles : candidates).push({ card: d, side: "right" });
      }
    }
    const pool = doubles.length > 0 ? doubles : candidates;
    if (pool.length === 0) {
      return { card: EmptyDomino, boardSide: "left" };
    }
    const pick = pool[Math.floor(Math.random() * pool.length)];
    return { card: pick.card, boardSide: pick.side };
  }

  async poseCard(): Promise<number> {
    const playable = this.hand.filter((d) => d !== EmptyDomino && d < 28);
    if (playable.length === 0) return EmptyDomino;
    const dbl = playable.filter(isDouble);
    const pool = dbl.length > 0 ? dbl : playable;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  async poseDoubleSix(): Promise<number> {
    return 27;
  }
}

export type PendingChoice =
  | { kind: "play"; resolve: (c: CardChoice) => void; event: GameEvent }
  | { kind: "pose"; resolve: (card: number) => void }
  | { kind: "poseDS"; resolve: (card: number) => void };

export class HumanPlayer implements Player {
  private hand: number[] = [];
  pending: PendingChoice | null = null;
  onPendingChange: (p: PendingChoice | null) => void = () => {};

  constructor(private name: string) {}

  getName(): string {
    return this.name;
  }
  getHand(): number[] {
    return this.hand;
  }
  setHand(h: number[]): void {
    this.hand = h.slice();
  }

  private setPending(p: PendingChoice | null) {
    this.pending = p;
    this.onPendingChange(p);
  }

  playCard(event: GameEvent): Promise<CardChoice> {
    return new Promise((resolve) => {
      this.setPending({
        kind: "play",
        event,
        resolve: (c) => {
          this.setPending(null);
          resolve(c);
        },
      });
    });
  }

  poseCard(): Promise<number> {
    return new Promise((resolve) => {
      this.setPending({
        kind: "pose",
        resolve: (card) => {
          this.setPending(null);
          resolve(card);
        },
      });
    });
  }

  poseDoubleSix(): Promise<number> {
    return new Promise((resolve) => {
      this.setPending({
        kind: "poseDS",
        resolve: (card) => {
          this.setPending(null);
          resolve(card);
        },
      });
    });
  }

  submitPlay(card: number, side: "left" | "right"): void {
    if (this.pending?.kind !== "play") return;
    this.pending.resolve({ card, boardSide: side });
  }

  submitPose(card: number): void {
    if (this.pending?.kind !== "pose") return;
    this.pending.resolve(card);
  }

  submitPoseDoubleSix(): void {
    if (this.pending?.kind !== "poseDS") return;
    this.pending.resolve(27);
  }
}
